import { format } from 'date-fns';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import find from 'lodash.find';
import get from 'lodash.get';
import React, { useRef } from 'react';

import BlogCardSection from '../components/Blog/BlogCardSection';
import BlogSeo from '../components/Blog/BlogSeo';
import BreadCrumbNav from '../components/Common/BreadCrumbNav';
import ContentContainer from '../components/Common/ContentContainer';
import FormBanner from '../components/Common/FormBanner';
import { ArrowLink, PrimaryLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import RichTextArticle from '../components/Common/RichText/RichTextArticle';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import * as styles from './blogPost.module.css';

const BlogPost = ({ data: { post, latestPostsByCategory, blogSettings }, pageContext, location }) => {
  // eslint-disable-next-line no-restricted-globals
  const share = location && location.href ? location.href : '';
  const postDate = format(new Date(post.publishDate), 'MMMM dd, yyyy');

  const mainCat = get(post, 'categories[0].categoryName', '');
  const readTime = post.readTime;
  const relatedPostsCat = find(
    latestPostsByCategory,
    (cat) => cat.categoryName === get(post, 'categories[0].categoryName')
  );
  const posts = get(relatedPostsCat, 'blogPosts', []);

  const relatedPosts = posts.filter((b) => b.slug !== post.slug).slice(0, 3);

  const scrollRef = useRef();
  const onScrollCta = (e) => {
    e.preventDefault();
    smoothScrollToRef(scrollRef);
  };
  const imageData = get(post, 'heroImage.gatsbyImageData');
  const isSvg = post.heroImage?.file?.contentType === 'image/svg';

  let hero =
    !!imageData && !isSvg ? (
      <GatsbyImage image={imageData} alt={post.title} />
    ) : (
      <img alt={post.title} src={get(post, 'heroImage.file.url', '')} />
    );

  const image = get(post, 'author.image.file.url');
  const authorImage = <img src={image} alt={'author'} />;
  const { ogImage, ...rest } = get(post, 'pageMeta', {}) || {};
  const imageBackup = get(ogImage, 'resize.src') || get(ogImage, 'file.url') || get(post, 'heroImage.file.url');
  const meta = {
    ogImageUrl: imageBackup,
    ...rest,
  };
  if (typeof window !== 'undefined' && !!window.FS) {
    window.FS.event('Blog Post Viewed', { category: mainCat });
  }

  /**
   * Tweak slightly for documentation demonstrating blog usage.
   */
  const isDocumentation = pageContext.documentation === true;

  return (
    <Layout isBlog={!isDocumentation} background={BACKGROUND.WHITE}>
      <BlogSeo meta={meta} slug={location.pathname} />
      <div className={'publish-date'} data-post-date={post.publishDate} />
      <ContentContainer>
        {!isDocumentation && <BreadCrumbNav shareUrl={share} shareTitle={post.title} />}
        {isDocumentation && (
          <nav className={styles.nav}>
            <PrimaryLink url={'/documentation/'} label={'Back to Documentation'} />
          </nav>
        )}
        <ScrollTo reference={scrollRef} />
        <div className={styles.header}>
          <div className={styles.hero}>{hero}</div>
          <div className={styles.heroContent}>
            <div>
              <span className={styles.author}>
                {mainCat} {mainCat && readTime && <>·</>} {readTime}
              </span>
              <h1>{post.title}</h1>
            </div>
            <span className={styles.author}>
              {get(post, 'author.name', 'FullStory')}
              <br />
              Posted {postDate}
            </span>
          </div>
        </div>
      </ContentContainer>
      <PageSection>
        <RichTextArticle
          copy={post.body}
          inlineCta={post.inlineCtaType}
          customInlineCta={{ copy: post.inlineCtaCustomCopy, link: post.inlineCtaCustomLink }}
        />

        {blogSettings.displayAuthorSection && (
          <div className={styles.authorSection}>
            <div className={styles.authorImage}>
              {image && authorImage}
              <div className={image ? styles.center : ''}>
                <span>{get(post, 'author.name')}</span>
                <span>{get(post, 'author.title')}</span>
              </div>
            </div>
            <div>
              <div>
                <h4>About the author</h4>
                <div dangerouslySetInnerHTML={{ __html: get(post, 'author.shortBio.childMarkdownRemark.html') }} />
              </div>
            </div>
          </div>
        )}

        <ArrowLink className={styles.arrowAdjust} label={'Return to top'} onClick={onScrollCta} isArrowUp={true} />
      </PageSection>
      {relatedPosts && relatedPosts.length > 0 && (
        <PageSection>
          <BlogCardSection header={'Related Posts'} posts={relatedPosts} />
        </PageSection>
      )}
      <FormBanner
        heading={blogSettings.newsletterSignupHeading}
        formId={blogSettings.marketoFormId}
        form={blogSettings.form}
        successMessage={get(blogSettings, 'form.successMessage')}
      />
    </Layout>
  );
};

export default BlogPost;

export const pageQuery = graphql`
  query GetBlogPost($slug: String!) {
    post: contentfulBlogPost(slug: { eq: $slug }) {
      ...BlogPost
    }
    blogSettings: contentfulBlogSettings(contentful_id: { eq: "6KdgnoVe8iL9MGRTTPgqyQ" }) {
      ...SiteSettings
    }
    latestPostsByCategory {
      categoryName
      blogPosts {
        ...BlogPostSlim
      }
    }
  }
`;
